import {useCallback, useState} from 'react';
import {ActivityIndicator, Modal, StyleSheet, Text, TouchableOpacity, View} from 'react-native';
import {useTranslation} from 'react-i18next';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import * as WebBrowser from 'expo-web-browser';
import {ExternalLink} from 'lucide-react-native';
import {useCurrentTheme} from '@/theming/useCurrentTheme';
import {ThemeType} from '@/theming/baseTheme';
import {useInvalidator, useOrganizationMembership} from '@/data/app-data-hooks';
import {AppDataMembership} from '@/data/app-data-interfaces';
import {createWebToken} from '@/data/app-data-api';
import {appConfig} from '@/app-config';
import {useHardwareBack} from '@/hooks/useHardwareBack';

/**
 * Org-scoped blocking gate: while the user's membership in this organization has
 * subscriptionStatus 'payment_required' (set when the membership was migrated from another
 * organization), a non-dismissible modal blocks the org UI until payment is set up.
 *
 * Payments must not happen in the app (app-store platform rules) — the gate hands the user off
 * to the web dashboard with an automatic web-token login, where the web payment gate takes
 * over. Completion is webhook-driven; the gate clears once /app-data reflects the new status.
 */
export const PaymentRequiredGate = ({orgId}: {orgId: string}) => {
    const {data: membership} = useOrganizationMembership(orgId);

    if (membership?.subscriptionStatus !== 'payment_required') {
        return null;
    }

    return <PaymentRequiredModal orgId={orgId} validUntil={membership.validUnit} migrationBonus={membership.migrationBonus}/>;
};

const PaymentRequiredModal = ({orgId, validUntil, migrationBonus}: {
    orgId: string,
    validUntil?: string,
    migrationBonus?: AppDataMembership['migrationBonus']
}) => {
    const {t, i18n} = useTranslation();
    const {theme} = useCurrentTheme();
    const insets = useSafeAreaInsets();
    const styles = createStyles(theme, insets);
    const {invalidateAppData} = useInvalidator();

    const [openingDashboard, setOpeningDashboard] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useHardwareBack(useCallback(() => true, []));

    const handleOpenDashboard = async () => {
        setError(null);
        setOpeningDashboard(true);
        try {
            const webTokenId = await createWebToken();
            // The handoff targets the react web dashboard (dashboardUrl), NOT the app's own
            // web deployment (webappUrl) — on the web version the latter would just render
            // this gate again.
            const dashboardUrl = appConfig.dashboardUrl;
            if (!dashboardUrl) {
                throw new Error('EXPO_PUBLIC_DASHBOARD_URL is not configured');
            }
            await WebBrowser.openBrowserAsync(`${dashboardUrl}/token/${webTokenId}?orgId=${orgId}`);
            // Payment completion is webhook-driven — refresh once the browser closes.
            await invalidateAppData();
        } catch {
            setError(t('membership.paymentRequired.error'));
        } finally {
            setOpeningDashboard(false);
        }
    };

    const handleCheckAgain = async () => {
        setRefreshing(true);
        try {
            await invalidateAppData();
        } finally {
            setRefreshing(false);
        }
    };

    const paidUntilDate = validUntil
        ? new Date(validUntil).toLocaleDateString(i18n.language, {dateStyle: 'medium'})
        : null;
    const bonusDeadlineDate = migrationBonus
        ? new Date(migrationBonus.deadline).toLocaleDateString(i18n.language, {dateStyle: 'medium'})
        : null;

    return (
        <Modal
            visible={true}
            transparent={true}
            animationType="slide"
            onRequestClose={() => undefined}
        >
            <View style={styles.modalOverlay}>
                <View style={styles.modalContent}>
                    <View style={styles.modalHeader}>
                        <Text style={styles.modalTitle}>{t('membership.paymentRequired.title')}</Text>
                        <Text style={styles.modalSubtitle}>{t('membership.paymentRequired.subtitle')}</Text>
                        {paidUntilDate && (
                            <Text style={styles.paidUntilNote}>
                                {t('membership.paymentRequired.paidUntilNote', {date: paidUntilDate})}
                            </Text>
                        )}
                        {migrationBonus && bonusDeadlineDate && (
                            <Text style={styles.bonusNote}>
                                {t('membership.paymentRequired.bonusOffer', {
                                    count: migrationBonus.bonusMonths,
                                    date: bonusDeadlineDate
                                })}
                            </Text>
                        )}
                    </View>

                    <View style={styles.body}>
                        <TouchableOpacity
                            style={styles.dashboardButton}
                            onPress={handleOpenDashboard}
                            disabled={openingDashboard}
                            activeOpacity={0.7}
                        >
                            <View style={styles.dashboardButtonContent}>
                                {openingDashboard
                                    ? <ActivityIndicator color={theme.colors.background} style={styles.buttonIcon}/>
                                    : <ExternalLink size={20} color={theme.colors.background} style={styles.buttonIcon}/>}
                                <Text style={styles.dashboardButtonText}>
                                    {t('membership.paymentRequired.openDashboard')}
                                </Text>
                            </View>
                        </TouchableOpacity>
                        <Text style={styles.dashboardHint}>
                            {t('membership.paymentRequired.openDashboardHint')}
                        </Text>
                    </View>

                    {error && <Text style={styles.errorText}>{error}</Text>}

                    <TouchableOpacity
                        style={styles.checkAgainButton}
                        onPress={handleCheckAgain}
                        disabled={refreshing}
                    >
                        {refreshing
                            ? <ActivityIndicator color={theme.colors.textSecondary}/>
                            : <Text style={styles.checkAgainText}>{t('membership.paymentRequired.checkAgain')}</Text>}
                    </TouchableOpacity>
                </View>
            </View>
        </Modal>
    );
};

const createStyles = (theme: ThemeType, insets: {bottom: number}) => StyleSheet.create({
    modalOverlay: {
        flex: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        justifyContent: 'flex-end',
    },
    modalContent: {
        backgroundColor: theme.colors.background,
        borderTopLeftRadius: 24,
        borderTopRightRadius: 24,
        paddingTop: 12,
        paddingBottom: insets.bottom + 20,
    },
    modalHeader: {
        paddingHorizontal: 24,
        paddingVertical: 20,
        borderBottomWidth: 1,
        borderBottomColor: theme.colors.border,
        marginBottom: 8,
    },
    modalTitle: {
        fontSize: 22,
        fontWeight: 'bold',
        color: theme.colors.text,
        marginBottom: 4,
    },
    modalSubtitle: {
        fontSize: 14,
        color: theme.colors.textSecondary,
    },
    paidUntilNote: {
        fontSize: 13,
        color: theme.colors.textSecondary,
        marginTop: 8,
        fontStyle: 'italic',
    },
    bonusNote: {
        fontSize: 13,
        color: theme.colors.primary,
        marginTop: 8,
        fontWeight: '600',
    },
    body: {
        paddingHorizontal: 20,
        paddingVertical: 8,
    },
    dashboardButton: {
        backgroundColor: theme.colors.primary,
        borderRadius: 12,
        padding: 18,
        alignItems: 'center',
    },
    dashboardButtonContent: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
    },
    buttonIcon: {
        marginRight: 12,
    },
    dashboardButtonText: {
        fontSize: 16,
        fontWeight: '600',
        color: theme.colors.background,
    },
    dashboardHint: {
        fontSize: 13,
        color: theme.colors.textSecondary,
        textAlign: 'center',
        marginTop: 12,
        lineHeight: 18,
    },
    errorText: {
        fontSize: 13,
        color: '#DC2626',
        textAlign: 'center',
        paddingHorizontal: 24,
        paddingBottom: 8,
    },
    checkAgainButton: {
        padding: 20,
        marginTop: 8,
        borderTopWidth: 1,
        borderTopColor: theme.colors.border,
        alignItems: 'center',
    },
    checkAgainText: {
        fontSize: 16,
        color: theme.colors.textSecondary,
        textAlign: 'center',
        fontWeight: '500',
    },
});
